<template>
  <ul v-if="chatList.length > 0" class="itp-used-area">
    <li v-for="item in chatList" :key="item.id" @click="goToChat(item)">
      <div class="left-col">
        <div class="logo-box">
          <img :src="getImageUrl(item.entranceLogoImageUrl)" alt="" @error="handleImageError" />
        </div>

        <div class="room-info">
          <div class="inr">
            <div>
              <span :class="`badge2 ${item.chatType}`">{{ t(`label_${item.chatType}`) }}</span>
              <span class="personnal">{{ t('label_capacity') }} {{ formatNumberWithComma(item.limitCount) }}</span>
            </div>
            <IconFavorite
              class="icon"
              :is-favorite="item.isFavorite"
              @toggle-favorite="$emit('toggle-favorite', item)"
            />
          </div>
          <p class="tit">{{ item.title }}</p>
        </div>
      </div>

      <div class="right-col">
        <button type="button" class="txt-btn" @click.stop="openChatMenuPopup(item)">{{ t('label_edit') }}</button>
        <p class="date">{{ item.displayTime }}</p>
      </div>
    </li>
  </ul>

  <div v-else class="no-data pd-vertical-7 no-data-flex">
    <div class="icon-area">
      <img :src="getAssetImageUrl('app/status/no-data.svg')" alt="" />
    </div>
    <span>{{ t('no_records') }}</span>
  </div>

  <Teleport to="body">
    <DropdownEdit
      v-if="isVisibleProfileDropdown"
      :isVisible="isVisibleProfileDropdown"
      :target-el="profileButtonRef"
      @close="isVisibleProfileDropdown = false"
    >
      <!--통역내역-->
      <button
        v-for="(menu, menuIndex) in SELF_SERVICE_DROPDOWN_MENU"
        :key="`self_service_menu_${menuIndex}`"
        class="button-medium button-white button-full-width-left"
        @click="onClickMenuOption(menu)"
      >
        <component :is="menu.iconComponent" />
        {{ t(menu.titleKey) }}
      </button>
    </DropdownEdit>
  </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import PopupBottomSheet from '@/components/domains/mobile/common/PopupBottomSheet.vue';
import PopupModal from '@/components/domains/mobile/common/PopupModal.vue';
import PopupChatMenu from '@/components/domains/mobile/popup/PopupChatMenu.vue';
import IconFavorite from '@/components/jh-app/icons/IconFavorite.vue';
import DropdownEdit from '@/components/shared/base/DropdownEdit.vue';
import { useCreateChat } from '@/composables/domains/mobile/useCreateChat';
import { usePopupStore } from '@/stores/popup';
import { ChatItem } from '@/types/components';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { formatNumberWithComma } from '@/utils/stringUtils.js';

const { t } = useI18n();
const router = useRouter();

const popupStore = usePopupStore();

const { getChatQuery, hasEnoughCredits } = useCreateChat();

withDefaults(
  defineProps<{
    chatList: ChatItem[];
  }>(),
  {
    chatList: () => [],
  }
);

const emits = defineEmits(['toggle-favorite', 'refresh']);

const getImageUrl = (url) => {
  return url ?? getAssetImageUrl('common/logos/logo-sconai.png');
};

const handleImageError = (event: Event) => {
  (event.target as HTMLImageElement).src = getAssetImageUrl('common/logos/logo-sconai.png');
};

const goToChat = async (item: ChatItem) => {
  try {
    if (!(await hasEnoughCredits(item.planId))) {
      popupStore.open(PopupModal, {
        title: t('error_credit_empty'),
        subTitle: t('credit_recharge_web'),
        confirmButton: { text: t('confirm') },
      });
      return;
    }

    const query = getChatQuery(item.code, item.planId);
    router.push({
      path: `/ss/chat/${item.callType.toLowerCase()}`,
      query,
    });
  } catch (error) {
    console.error('크레딧 확인 실패: ', error);
  }
};

const openChatMenuPopup = (chat: ChatItem) => {
  popupStore.open(PopupBottomSheet, {
    contentSlot: h(PopupChatMenu),
    payload: chat,
    showHandleBar: true,
    refresh: () => emits('refresh'),
  });
};
const isVisibleProfileDropdown = ref(false);
const profileButtonRef = ref<HTMLElement>(); // 프로필 버튼 Ref
</script>

<style scoped lang="scss">
.itp-used-area {
  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 0;
    .left-col {
      display: flex;
      align-items: center;
      gap: $size-s;
      flex: 6;
      .logo-box {
        width: 48px;
        height: 48px;
        border-radius: 1rem;
        border: 1px solid $color-gray-40;
        overflow: hidden;
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
      .room-info {
        .inr {
          display: flex;
          align-items: center;
          gap: $size-s;
          margin-bottom: 2px;
          div {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: $size-xs;
            .personnal {
              color: $color-gray-200;
              font-size: $font-size-xs;
            }
          }
        }
        .tit {
          font-size: $font-size-psmall;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          max-width: 140px;
        }
      }
    }
    .right-col {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      flex: 4;
      .txt-btn {
        font-size: $font-size-psmall;
        padding: 0.5rem 0 0.5rem 0.5rem;
        max-height: 32px;
        text-align: right;
      }
      .date {
        color: $color-gray-200;
        font-size: $font-size-xs;
        text-align: right;
        min-height: 19.5px;
      }
    }
  }
}
</style>
